<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useAuthStore } from '../../stores/auth';
import { useCatalogueStore } from '../../stores/catalogue';
import { useProfilesStore } from '../../stores/profiles';
import { useBlocksStore } from '../../stores/blocks';
import { computeProfileScore } from '../../lib/profileScore';
import { storeToRefs } from 'pinia';
import { db } from '../../lib/firebase';
import { collection, query, where, getDocs, or, limit, orderBy } from 'firebase/firestore';
import { optimizedUrl, optimizedSrcset } from '../../utils/imageUrl';
import AtelierHomeSection from '../../components/ateliers/AtelierHomeSection.vue';
import { useAteliersStore } from '../../stores/ateliers';
import {
  Utensils, ArrowRight, ShoppingBag, Clock, Package, Star, AlertTriangle,
  CheckCircle2, ChefHat, Sparkles, Truck, Euro, Building2, Armchair,
  ShieldCheck, Check, GraduationCap, Salad
} from "lucide-vue-next";
import ProfilesOnboardingModal from '../../components/profiles/ProfilesOnboardingModal.vue';
import BeforeAfterFade from '../../components/marketing/BeforeAfterFade.vue';
import BadgesReassurance from '../../components/BadgesReassurance.vue';
import BrandTitle from '../../components/brand/BrandTitle.vue';
import HighlightMark from '../../components/brand/HighlightMark.vue';
import Eyebrow from '../../components/brand/Eyebrow.vue';
import IconDisc from '../../components/brand/IconDisc.vue';
import SectionHeading from '../../components/brand/SectionHeading.vue';
import platsDessusWide from '../../assets/flyer/plats-dessus-wide.webp';
import platsDessusMobile from '../../assets/flyer/plats-dessus-mobile.webp';
import HowItWorksFlyer from '../../components/marketing/HowItWorksFlyer.vue';
import conceptPortions from '@/assets/concept-portions.webp';

// Visuel de repli de la section sous-vide : un plat du catalogue (photo de plat de secours sinon).
const sousVideFallbackImage = computed(() => {
  const all = catalogueStore.dishes || [];
  const d = all.find((x) => x && x.image && x.active !== false && _ctaPhotogenicIds.includes(x.id)) || all.find((x) => x && x.image && x.active !== false);
  return d ? (optimizedUrl(d.image, { width: 640, quality: 78 }) || d.image) : conceptPortions;
});


const authStore = useAuthStore();const catalogueStore = useCatalogueStore();
const profilesStore = useProfilesStore();
const blocksStore = useBlocksStore();
const { profiles: householdProfiles } = storeToRefs(profilesStore);
const { blocks } = storeToRefs(blocksStore);
const { dishes: catalogueDishes } = storeToRefs(catalogueStore);

// Preload Products immediately on initialization (even before mount)
catalogueStore.fetchProducts();
catalogueStore.fetchReviews();

watch(() => authStore.user?.uid, (uid) => {
  if (uid) {
    profilesStore.bind(uid);
    if (!blocks.value?.length) blocksStore.fetchBlocks();
  }
}, { immediate: true });

const leastTunedProfile = computed(() => {
  if (!authStore.isLoggedIn) return null;
  const candidates = householdProfiles.value
    .map((p) => {
      const info = computeProfileScore(p, blocks.value || [], catalogueDishes.value || []);
      return { id: p.id, name: p.name, ageGroup: p.ageGroup, score: info.score, label: info.label, color: info.color };
    })
    .filter((p) => p.score < 100);
  if (candidates.length === 0) return null;
  return candidates.reduce((min, p) => (p.score < min.score ? p : min), candidates[0]);
});

// --- STATE ---
const heroImageLoaded = ref(false);



const entrepriseImageLoaded = ref(false);
const parrainageImageLoaded = ref(false);
const extraImagesLoaded = ref(new Map()); // Track loading state per extra image URL

// Hardcoded images for critical path - defer preload to not compete with LCP
const entrepriseOfferImage = '/img/entreprise-offer.webp';
const parrainageOfferImage = '/img/parrainage-offer.webp';

const onExtraImageLoad = (url) => {
  extraImagesLoaded.value = new Map(extraImagesLoaded.value.set(url, true));
};
const isExtraImageLoaded = (url) => !!extraImagesLoaded.value.get(url);

// --- PRELOAD CRITICAL IMAGES ---
const preloadImage = (src) => {
  if (typeof Image !== 'undefined') {
    const img = new Image();
    img.src = src;
  }
};

// Prefetch all visible extra images as soon as their URLs are known
const _prefetchedExtras = new Set();
watch(() => catalogueStore.extras, (newExtras) => {
  if (!newExtras?.length) return;
  const activeExtras = newExtras.filter(e => e.active !== false && e.image);
  for (const extra of activeExtras) {
    const optUrl = optimizedUrl(extra.image, { width: 400, quality: 75 });
    const url = optUrl || extra.image;
    if (!_prefetchedExtras.has(url)) {
      _prefetchedExtras.add(url);
      // Use link preload for better browser scheduling (non-blocking)
      if (typeof document !== 'undefined') {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.as = 'image';
        link.href = url;
        document.head.appendChild(link);
      }
    }
  }
}, { immediate: true });

// Defer preload of below-fold images to not compete with LCP on slow connections
if ('requestIdleCallback' in window) {
  requestIdleCallback(() => { preloadImage(entrepriseOfferImage); preloadImage(parrainageOfferImage); });
} else {
  setTimeout(() => { preloadImage(entrepriseOfferImage); preloadImage(parrainageOfferImage); }, 2000);
}

const userType = ref('b2b'); // 'b2b' or 'b2c' (simulated)
// showBanner controlled by scroll removed - always visible
const lastOrder = ref(null);
const loadingOrder = ref(false); // Default to false, controlled by fetch

// Function to fetch orders
const fetchOrders = async () => {
    if (!authStore.isLoggedIn || !authStore.user) {
        lastOrder.value = null;
        loadingOrder.value = false;
        return;
    }

    loadingOrder.value = true;
    try {
        const q = query(
            collection(db, 'orders'),
            or(
                where('userId', '==', authStore.user.uid),
                where('clientId', '==', authStore.user.uid)
            )
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
            // Sort client-side to avoid compound index issues for now
            const sorted = snap.docs.map(d => ({ 
                id: d.id, 
                ...d.data(), 
                createdAt: d.data().createdAt?.toDate?.() || new Date(d.data().createdAt) 
            })).sort((a, b) => b.createdAt - a.createdAt);
            
            lastOrder.value = sorted[0];
        } else {
            lastOrder.value = null;
        }
    } catch (e) {
        console.error("Home Order Fetch Error", e);
    } finally {
        loadingOrder.value = false;
    }
};

// Watch for auth changes to trigger fetch
watch(() => authStore.isLoggedIn, (isLoggedIn) => {
  if (isLoggedIn) {
      fetchOrders();
  } else {
      lastOrder.value = null;
      loadingOrder.value = false;
  }
}, { immediate: true });

const activeSlide = ref(0); // For Bento Block 3

const scrollToSlide = (index) => {
    activeSlide.value = index;
};

// Timeline Logic
const orderSteps = [
  { id: 'pending_validation', label: 'Attente', icon: Clock, color: 'amber' },
  { id: 'confirmed', label: 'Validée', icon: CheckCircle2, color: 'emerald' },
  { id: 'preparing', label: 'Cuisine', icon: ChefHat, color: 'pink' },
  { id: 'shipping', label: 'Livraison', icon: Truck, color: 'blue' },
  { id: 'delivered', label: 'Reçue', icon: CheckCircle2, color: 'emerald' }
];

const getCurrentStepIndex = (status) => {
    const s = (status || '').toLowerCase();
    if (['cancelled', 'annulée'].includes(s)) return -1;
    if (['completed', 'delivered', 'livrée', 'livré'].includes(s)) return 4;
    if (['delivering', 'shipping', 'en livraison', 'shipped'].includes(s)) return 3;
    if (['preparing', 'en cuisine', 'en préparation'].includes(s)) return 2;
    if (['confirmed', 'paid', 'validée'].includes(s)) return 1;
    if (['pending_reschedule'].includes(s)) return 0; // Treated as initial step but with warning
    return 0; // pending_validation
};

const getStepColor = (state, colorReq, status) => {
  // Special Case Reschedule
  if (status === 'pending_reschedule' && state === 'active') return 'text-white bg-red-500 ring-red-100 shadow-lg shadow-red-500/30 animate-pulse';

  if (state === 'pending') return 'text-gray-400 bg-gray-100 ring-white';
  if (state === 'completed') return 'text-white bg-[#2ec4b6] ring-white border-white'; 
  
  // Active state colors
  if (colorReq === 'pink') return 'text-white bg-[#e94591] ring-white shadow-lg shadow-[#e94591]/30';
  if (colorReq === 'blue') return 'text-white bg-[#0077B6] ring-white shadow-lg shadow-[#0077B6]/30';
  if (colorReq === 'amber') return 'text-[#111] bg-[#ffd93e] ring-white shadow-lg shadow-[#ffd93e]/30';
  return 'text-white bg-[#2ec4b6] ring-white shadow-lg shadow-[#2ec4b6]/30';
};

const getStepState = (index, currentIdx) => {
  if (index < currentIdx) return 'completed';
  if (index === currentIdx) return 'active';
  return 'pending';
};

let slideInterval;

let preloadLink = null;
const ateliersStore = useAteliersStore();
// Le bouton d'appel vers les ateliers n'a de sens que s'il y en a un
// d'ouvert. Le fetch n'est lance que si personne ne l'a deja fait
// (AtelierHomeSection, monte avant, s'en charge le plus souvent).
onMounted(() => {
  if (!ateliersStore.upcomingAteliers.length && !ateliersStore.loading) ateliersStore.fetchUpcoming(1);
});

onMounted(async () => {
  // Preload fallback image only on Home page
  preloadLink = document.createElement('link');
  preloadLink.rel = 'preload';
  preloadLink.href = '/img/dish-fallback.webp';
  preloadLink.as = 'image';
  preloadLink.type = 'image/webp';
  document.head.appendChild(preloadLink);

  // Auto-slide for Bento
  slideInterval = setInterval(() => {
      const nextSlide = activeSlide.value === 0 ? 1 : 0;
      scrollToSlide(nextSlide);
  }, 6000);

  // Auto-slide for Extras
  extraSlideInterval.value = setInterval(() => {
      if (extrasList.value.length > 1) {
          activeExtraSlide.value = (activeExtraSlide.value + 1) % extrasList.value.length;
      }
  }, 7000);

  // Rotation de la card sous-vide mobile (toutes les ~13s, après que le
  // composant a eu le temps de faire son crossfade sachet/assiette interne).
  _mobileSousVideTimer = setInterval(() => {
    const n = sousVideMobilePool.value.length;
    if (n > 1) mobileSousVideIndex.value = (mobileSousVideIndex.value + 1) % n;
  }, 13000);

  // Auto-slide des 3 carousels mobiles (Comment ça marche, Testimonials,
  // Va plus loin). Toutes les 6.5s, mais uniquement quand l'écran est étroit
  // (en desktop ce sont des grilles, pas des carousels). Pauseé pendant ~9s
  // après une interaction tactile pour ne pas couper le swipe utilisateur.
  _carouselsTimer = setInterval(() => {
    const now = Date.now();
    if (now - _lastUserInteractAt < 9000) return;
    // Testimonials : défilement auto sur toutes tailles (carrousel partout).
    _slideNextByCard(testimonialsCarousel.value);
    // Les autres carousels ne défilent qu'en mobile (desktop = grilles).
    if (window.innerWidth >= 768) return;
    _slideNext(sousVideCarousel.value);
    _slideNext(howtoCarousel.value);
    _slideNext(vaplusloinCarousel.value);
  }, 6500);

  // Marquage de l'interaction tactile/clic sur chaque carousel pour suspendre
  // l'auto-slide pendant que l'utilisateur fait défiler manuellement.
  [sousVideCarousel.value, howtoCarousel.value, testimonialsCarousel.value, vaplusloinCarousel.value].forEach((el) => {
    if (!el) return;
    el.addEventListener('touchstart', _markInteract, { passive: true });
    el.addEventListener('pointerdown', _markInteract, { passive: true });
  });
});

onUnmounted(() => {
  if (slideInterval) clearInterval(slideInterval);
  if (extraSlideInterval.value) clearInterval(extraSlideInterval.value);
  if (_mobileSousVideTimer) { clearInterval(_mobileSousVideTimer); _mobileSousVideTimer = null; }
  if (_carouselsTimer) { clearInterval(_carouselsTimer); _carouselsTimer = null; }
  if (preloadLink && preloadLink.parentNode) {
    preloadLink.parentNode.removeChild(preloadLink);
    preloadLink = null;
  }
});

const parrainageOffer = {
  title: 'Parraine tes amis',
  subtitle: 'Gagne -10% sur ta prochaine commande !',
  image: parrainageOfferImage // use preloaded var const
};

// True quand au moins un extra est actif dans le catalogue. On regarde
// directement le store car extrasList renvoie un placeholder même quand
// la liste est vide (sinon on remplace le slider extras par un fallback
// parrainage / entreprise).
const hasExtras = computed(() => (catalogueStore.extras || []).some(e => e.active !== false));

// Recettes ayant une image catalogue ET une image sous-vide générée :
// utilisées pour la démo crossfade "Livré sous-vide -> Prêt à déguster".
// Le tri est aléatoire mais stable pendant la session (seed = Date.now() au
// montage), donc à chaque rechargement de la page on découvre d'autres plats.
const _sousVideSeed = Date.now();
function _hashSeeded(s, seed) {
  let h = seed | 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return h;
}
// Plats "sans grosse sauce" (poisson rôti, viande grillée, etc.) qu'on force
// dans la sélection pour ne pas afficher que des plats mijotés/en sauce.
// Cette liste est volontairement locale ; si elle se vide on tombe sur du
// tirage simple. À enrichir au fur et à mesure qu'on génère plus d'images.
const _drierDishIds = new Set([
  '7hwsLUPXo4QbowsRgagb', // Truite laquée gingembre, trio de riz, fondue poireaux
  'bk65lnVkhr4twev3omqs', // Pavé saumon, galettes PDT, fondue poireaux
  'fHvifCk08XYZEo6HJ0Dd'  // Pavé saumon, PDT grenaille, asperges rôties
]);

const sousVideShowcase = computed(() => {
  const all = catalogueStore.dishes || [];
  const eligible = all.filter((d) => d && d.image && d.sousVideImage && d.sousVideVitrine === true && d.active !== false);
  if (eligible.length === 0) return [];

  const sortFn = (a, b) => _hashSeeded(a.id || '', _sousVideSeed) - _hashSeeded(b.id || '', _sousVideSeed);
  const driers = [...eligible.filter((d) => _drierDishIds.has(d.id))].sort(sortFn);
  const others = [...eligible.filter((d) => !_drierDishIds.has(d.id))].sort(sortFn);

  // Compose 3 plats : 1 "sec" si dispo + 2 autres, complète avec ce qui reste.
  const picked = [];
  if (driers.length > 0) picked.push(driers[0]);
  picked.push(...others.slice(0, 3 - picked.length));
  if (picked.length < 3) picked.push(...driers.slice(1, 1 + (3 - picked.length)));
  return picked.slice(0, 3);
});

// Sur mobile on n'affiche qu'une carte qui CYCLE entre tous les plats
// éligibles (pas seulement les 3 sélectionnés pour desktop). Permet de gagner
// énormément de scroll sans perdre la variété visuelle.
const sousVideMobilePool = computed(() => {
  const all = catalogueStore.dishes || [];
  const eligible = all.filter((d) => d && d.image && d.sousVideImage && d.sousVideVitrine === true && d.active !== false);
  // On garde un ordre stable basé sur le seed (pas de re-shuffle pendant la session)
  const sortFn = (a, b) => _hashSeeded(a.id || '', _sousVideSeed) - _hashSeeded(b.id || '', _sousVideSeed);
  return [...eligible].sort(sortFn);
});
const mobileSousVideIndex = ref(0);
let _mobileSousVideTimer = null;
const featuredMobileDish = computed(() => {
  const list = sousVideMobilePool.value;
  if (list.length === 0) return null;
  return list[mobileSousVideIndex.value % list.length];
});

// Refs des carousels horizontaux mobile pour pouvoir les auto-slider.
const sousVideCarousel = ref(null);
const howtoCarousel = ref(null);
const testimonialsCarousel = ref(null);
const vaplusloinCarousel = ref(null);
// Timestamp du dernier touch/clic sur un carousel : sert à pauser l'auto-slide
// pendant ~9s pour ne pas casser un swipe utilisateur en cours.
let _lastUserInteractAt = 0;
function _markInteract() { _lastUserInteractAt = Date.now(); }
// Index actif de chaque carousel (pour les dots dynamiques sous chaque section).
const sousVideActiveIndex = ref(0);
const howtoActiveIndex = ref(0);
const testimonialsActiveIndex = ref(0);
const vaplusloinActiveIndex = ref(0);
let _carouselsTimer = null;
function _slideNext(el) {
  if (!el) return;
  const max = el.scrollWidth - el.clientWidth;
  if (max <= 0) return;
  const next = el.scrollLeft + el.clientWidth;
  el.scrollTo({ left: next >= max - 10 ? 0 : next, behavior: 'smooth' });
}
// Défilement carte par carte (pour les testimonials : carrousel sur toutes tailles).
function _slideNextByCard(el) {
  if (!el) return;
  const max = el.scrollWidth - el.clientWidth;
  if (max <= 1) return;
  const card = el.querySelector('[data-card]');
  const step = card ? card.offsetWidth + 16 : el.clientWidth; // +16 = gap-4
  const next = el.scrollLeft + step;
  el.scrollTo({ left: next >= max - 4 ? 0 : next, behavior: 'smooth' });
}
function _slideTo(el, idx) {
  if (!el) return;
  el.scrollTo({ left: idx * el.clientWidth, behavior: 'smooth' });
}
// Met à jour l'index actif au scroll. Handlers dédiés par carousel : les refs
// sont auto-unwrap dans les handlers du template, on accède donc directement
// aux refs via la closure du script setup plutôt que via paramètres.
function _syncIndex(el, indexRef) {
  if (!el) return;
  const idx = Math.round(el.scrollLeft / el.clientWidth);
  if (indexRef.value !== idx) indexRef.value = idx;
}
function _onSousVideScroll() { _syncIndex(sousVideCarousel.value, sousVideActiveIndex); }
function _onHowtoScroll() { _syncIndex(howtoCarousel.value, howtoActiveIndex); }
function _onTestimonialsScroll() { _syncIndex(testimonialsCarousel.value, testimonialsActiveIndex); }
function _onVaplusloinScroll() { _syncIndex(vaplusloinCarousel.value, vaplusloinActiveIndex); }

// Note moyenne et nombre total d'avis pour le badge "preuve sociale"
// affiché en haut des testimonials. On filtre les ratings invalides
// (notes < 1 ou > 5, ou non numériques) pour ne pas fausser la moyenne.
const reviewsStats = computed(() => {
  const all = catalogueStore.reviews || [];
  const valid = all.filter((r) => {
    const n = Number(r.rating);
    return Number.isFinite(n) && n >= 1 && n <= 5;
  });
  if (valid.length === 0) return null;
  const sum = valid.reduce((acc, r) => acc + Number(r.rating), 0);
  return {
    count: valid.length,
    average: (sum / valid.length).toFixed(1)
  };
});

// Affichage du nombre d'avis arrondi à la dizaine supérieure ("30+ avis"
// plutôt que "28") : plus crédible pour un visiteur et toujours honnête.
// Tant qu'on est sous 10 avis valides on affiche le nombre exact pour rester
// transparent ; au-dessus on arrondit.
const reviewsCountLabel = computed(() => {
  if (!reviewsStats.value) return '…';
  const n = reviewsStats.value.count;
  if (n < 10) return String(n);
  return `${Math.ceil(n / 10) * 10}+`;
});

// Image de plat utilisée à droite du CTA final "Prêt à goûter ?". On vise un
// plat "marketing-friendly" (poisson, présentation soignée) plutôt que le
// premier venu. Whitelist d'IDs photogéniques + fallback hero statique.
const _ctaPhotogenicIds = ['fHvifCk08XYZEo6HJ0Dd', 'bk65lnVkhr4twev3omqs', '7hwsLUPXo4QbowsRgagb'];
const ctaBackgroundImage = computed(() => {
  const all = catalogueStore.dishes || [];
  // On essaie d'abord la whitelist (saumon/truite), puis fallback sur le premier dispo
  for (const id of _ctaPhotogenicIds) {
    const found = all.find((d) => d && d.id === id && d.image && d.active !== false);
    if (found) return found.image;
  }
  const any = all.find((d) => d && d.image && d.active !== false);
  return any?.image || '/img/hero.webp';
});

const activeExtraSlide = ref(0);
const extraSlideInterval = ref(null);

const extrasList = computed(() => {
   // Filter only active extras
   const activeExtras = catalogueStore.extras.filter(e => e.active !== false);
   
   let results = [];
   if (activeExtras.length > 0) {
       // Sort by Updated/Created date descending (newest first)
       const sortedExtras = [...activeExtras].sort((a, b) => {
           const getVal = (item) => {
              const parse = (v) => {
                if (!v) return 0;
                if (typeof v === 'number') return v;
                if (v.toDate) return v.toDate().getTime();
                const ms = new Date(v).getTime();
                return isNaN(ms) ? 0 : ms;
              };
              return parse(item.updatedAt) || parse(item.createdAt) || 0;
           };
           return getVal(b) - getVal(a);
       });
       
       results = sortedExtras.map(extra => {
           const priceVal = Number(extra.price);
           return {
               name: extra.name,
               description: extra.description,
               price: extra.price ? (Number.isInteger(priceVal) ? `${priceVal}€` : `${priceVal.toFixed(2)}€`) : '',
               image: extra.image || null // No ugly placeholder, skeleton will show
           };
       });
   }
   
   // No preload for extras, they are lazy-loaded below the fold

   if (results.length > 0) return results;

   // Fallback, no image, skeleton will display
   return [{
      name: 'Extra du moment',
      description: 'Découvrez notre petite douceur de la semaine.',
      price: '',
      image: null
   }];
});

// Cache for the random hero image to prevent flash on re-render/navigate
// Try to recover from session storage to show image IMMEDIATELY (before Firestore loads)
const cachedHeroImage = ref(typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('clocook_hero_img') : null);

// Allow dynamic hero swap only after initial paint to preserve LCP with static fallback
const heroSwapReady = ref(false);
onMounted(() => {
  // Delay hero swap so the preloaded static fallback remains the LCP element
  // requestIdleCallback waits until browser is idle (after paint)
  const enable = () => { heroSwapReady.value = true; };
  if ('requestIdleCallback' in window) {
    requestIdleCallback(enable, { timeout: 4000 });
  } else {
    setTimeout(enable, 3000);
  }
});

const heroImage = computed(() => {
    // Return cached if we have it to avoid "flicker" of new random or waiting for firestore
    if (cachedHeroImage.value) return cachedHeroImage.value;

    // Include all dishes (active or inactive) as requested
    const dishesWithImages = (catalogueStore.dishes || []).filter(d => d.image);
    
    // Fast fallback: return a preloaded static image while waiting for Firestore
    // This dramatically improves LCP on slow connections (mobile Lighthouse)
    if (dishesWithImages.length === 0) return '/img/dish-fallback.webp';

    // Don't swap to dynamic image until the browser is idle (preserves LCP)
    if (!heroSwapReady.value) return '/img/dish-fallback.webp';

    // Exclude the extrasList images to avoid duplicates
    const extraImages = new Set(extrasList.value.map(e => e.image));
    const potentialImages = dishesWithImages
        .filter(d => !extraImages.has(d.image));

    let result = null;
    if (potentialImages.length === 0) {
        const first = dishesWithImages[0];
        result = first.image || first.thumbnail;
    } else {
        // Pick a random dish
        const randomDish = potentialImages[Math.floor(Math.random() * potentialImages.length)];
        // Prioritize full image for quality (Home Hero is large)
        result = randomDish.image || randomDish.thumbnail;
    }

    if (result) {
        // Preload the OPTIMIZED version (not the raw Storage URL)
        const opt = optimizedUrl(result, { width: 600, quality: 75 });
        preloadImage(opt || result);
        cachedHeroImage.value = result; // Lock it for session
        if (typeof sessionStorage !== 'undefined') {
             sessionStorage.setItem('clocook_hero_img', result);
        }
    }
    return result;
});

// Optimized hero image URL & srcset (resized via Cloud Function proxy)
const optimizedHeroUrl = computed(() => optimizedUrl(heroImage.value, { width: 600, quality: 75 }));
const heroSrcset = computed(() => optimizedSrcset(heroImage.value, [400, 600, 800]));
// Helper for extra images
const getOptimizedExtraUrl = (url) => optimizedUrl(url, { width: 400, quality: 75 });
const getExtraSrcset = (url) => optimizedSrcset(url, [300, 400, 600]);

const entrepriseOffer = {
  title: 'Pour tes équipes',
  subtitle: 'Fais-toi livrer au bureau sans frais supplémentaires.',
  image: entrepriseOfferImage, // use preloaded var const
  link: '/entreprise'
};

// Derniers retours clients : vrais avis Firestore "complets" (avec commentaire).
// Miniature du plat + nom client tronqué + note + date.
const latestReviews = computed(() => {
  const products = new Map();
  for (const p of [...(catalogueStore.dishes || []), ...(catalogueStore.extras || [])]) products.set(p.id, p);
  const truncName = (raw) => {
    const s = String(raw || '').replace(/[._]+/g, ' ').trim();
    if (!s) return 'Client';
    const parts = s.split(/\s+/).filter(Boolean);
    const cap = (w) => w.charAt(0).toUpperCase() + w.slice(1);
    return cap(parts[0]) + (parts[1] ? ' ' + parts[1].charAt(0).toUpperCase() + '.' : '');
  };
  const ratingOf = (r) => Number(r.dishRating || r.rating || 0);
  const dateMs = (r) => r.createdAt?.toMillis ? r.createdAt.toMillis() : (r.createdAt?.seconds ? r.createdAt.seconds * 1000 : 0);
  const seen = new Set(); // 1 seul avis par client : le plus récent
  return (catalogueStore.reviews || [])
    .filter((r) => !r.hiddenFromHome)         // avis masqués par l'admin (curation vitrine)
    .filter((r) => (r.comment || '').trim()) // avis complets (avec commentaire)
    .filter((r) => ratingOf(r) >= 5)          // puis seulement les avis 5★ (rien en dessous)
    .sort((a, b) => dateMs(b) - dateMs(a))    // du plus récent au plus ancien
    // Dédup par client APRES le filtre de note : sinon un avis récent non
    // éligible (ex. 3★) consommait la place du client et bloquait ses avis
    // éligibles plus anciens (aucun avis du client ne sortait).
    .filter((r) => {
      const key = r.userId || r.reviewerName || r.userName || r.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 10)
    .map((r) => {
      const p = products.get(r.productId || r.dishId);
      const ms = dateMs(r);
      return {
        id: r.id,
        // Plat noté (nom servi enregistré avec l'avis) avant la fiche actuelle, qui a pu changer.
        dishName: r.productName || (p && p.name) || 'Plat',
        dishImage: (p && (p.thumbnail || p.image)) || null,
        name: truncName(r.reviewerName || r.userName),
        text: r.comment,
        rating: ratingOf(r),
        date: ms ? new Date(ms).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : ''
      };
    });
});

// --- ACTIONS ---
const toggleLogin = () => {
  if (authStore.isLoggedIn) {
    authStore.logout();
  } else {
    authStore.login();
  }
};

const toggleUserType = () => {
  userType.value = userType.value === 'b2b' ? 'b2c' : 'b2b';
  if (authStore.user) {
    authStore.user.type = userType.value;
  }
};

const isOrderActive = computed(() => {
    if (!lastOrder.value) return false;
    const s = (lastOrder.value.status || '').toLowerCase();
    return !['cancelled', 'annulée', 'completed', 'delivered', 'livrée', 'livré'].includes(s);
});

</script>

<template>
  <ProfilesOnboardingModal v-if="authStore.isLoggedIn" />
  <div class="space-y-4 max-w-7xl mx-auto px-4 md:px-6 pt-2 md:pt-4 relative pb-6 md:pb-0">

    <!-- Mobile : bandeau défilant, identique à l'accueil natif. Rendu trop
         étiré sur grand écran, on garde donc la pastille d'origine au-delà de
         md, où elle sert aussi de lien vers la page Livraison. -->
    <BadgesReassurance class="md:hidden w-full" />

    <!-- Top Banner: Livraison Gratuite (SEO Optimized), desktop uniquement -->
    <RouterLink to="/livraison" class="hidden md:block w-full max-w-7xl mx-auto group no-underline hover:opacity-95 transition-opacity">
      <div class="bg-[#ffd93e] text-[#111] text-center py-2 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 rounded-xl md:rounded-full mx-auto w-fit px-6 md:px-8 shadow-sm relative overflow-hidden group-hover:shadow-md transition-shadow z-20 whitespace-normal md:whitespace-nowrap mt-0" role="alert">
          <Truck class="w-3.5 h-3.5 shrink-0" />
          <span class="truncate">Livraison GRATUITE sur <strong class="font-black">Annecy</strong></span>
      </div>
    </RouterLink>

    <!-- Main Grid Layout -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">

      <!-- BLOCK 1: HERO, colonne de gauche sur toute la hauteur, composition
           du recto du flyer : photo des plats vus du dessus en haut, panneau
           jaune au coin haut droit arrondi qui recouvre la photo, slogan sur
           trois lignes avec ses pastilles, appel à l'action. -->
      <article class="col-span-1 md:col-span-2 md:row-span-2 bg-white rounded-[32px] relative overflow-hidden shadow-sm border border-gray-100 flex flex-col">
        <div class="relative h-52 sm:h-60 md:h-64 lg:h-72 shrink-0 overflow-hidden bg-[#efe9db]">
          <picture>
            <source media="(min-width: 768px)" :srcset="platsDessusWide" />
            <img :src="platsDessusMobile" alt="Plats Clo'Cook vus du dessus" width="900" height="675"
                 loading="eager" fetchpriority="high" decoding="async"
                 class="w-full h-full object-cover object-center" />
          </picture>
        </div>
        <div class="relative z-10 bg-[#ffd93e] rounded-tr-[64px] -mt-10 md:-mt-12 px-6 py-8 md:px-10 md:py-10 flex-1 flex flex-col justify-center gap-6">
          <div>
            <p v-if="authStore.isLoggedIn" class="text-base md:text-lg text-[#111]/70 font-bold mb-3">Bonjour <HighlightMark tone="maison">{{ authStore.user.firstName }}</HighlightMark>,</p>
            <h1 class="font-baloo font-extrabold text-[#111] text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.12]">
              Des repas <HighlightMark tone="maison">sains</HighlightMark><br>
              et <HighlightMark tone="maison" :delay="330">personnalisés</HighlightMark>,<br>
              livrés chez toi.
            </h1>
          </div>
          <div>
            <RouterLink :to="authStore.isLoggedIn && authStore.user.type === 'b2b' ? '/commande/entreprise' : '/commande/maison'"
                        class="btn-flyer group inline-flex items-center gap-3 bg-white text-[#111] pl-2 pr-6 py-2 rounded-full font-extrabold text-base shadow-[0_8px_24px_rgba(0,0,0,0.12)] hover:shadow-[0_12px_28px_rgba(233,69,145,0.35)] hover:-translate-y-0.5 active:scale-95 transition-all">
              <span class="btn-flyer-disc w-10 h-10 rounded-full bg-[#e94591] text-white flex items-center justify-center">
                <Utensils class="w-5 h-5" />
              </span>
              <span class="relative">Je commande</span>
              <ArrowRight class="btn-flyer-arrow w-4 h-4 text-[#e94591]" />
            </RouterLink>
          </div>
          <div class="hidden md:flex flex-wrap gap-2 pt-1">
            <span class="inline-flex items-center gap-1.5 bg-white rounded-full px-3 py-1.5 text-xs font-bold text-[#111]">
              <Truck class="w-3.5 h-3.5 text-[#e94591]" /> Livraison offerte à Annecy
            </span>
            <span class="inline-flex items-center gap-1.5 bg-white rounded-full px-3 py-1.5 text-xs font-bold text-[#111]">
              <Euro class="w-3.5 h-3.5 text-[#e94591]" /> Dès 14 € le repas
            </span>
            <span class="inline-flex items-center gap-1.5 bg-white rounded-full px-3 py-1.5 text-xs font-bold text-[#111]">
              <Check class="w-3.5 h-3.5 text-[#e94591]" /> Sans engagement
            </span>
          </div>
        </div>
      </article>

      <!-- BLOCK 2: INFO / TRACKING -->
      <article class="col-span-1 md:col-span-2 bg-white rounded-[32px] p-0 relative overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[translate,box-shadow] duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] will-change-transform group border border-gray-100 flex flex-col h-full min-h-[260px] md:min-h-[240px]">
         <template v-if="authStore.isLoggedIn && (loadingOrder || (lastOrder && isOrderActive))">
            <div v-if="loadingOrder" class="p-6 h-full flex flex-col justify-center items-center relative z-10 bg-white min-h-[160px]">
                <div class="animate-spin rounded-full h-8 w-8 border-4 border-gray-100 border-t-[#e94591] mb-2"></div>
                <p class="text-xs text-gray-400 font-bold animate-pulse">Chargement...</p>
            </div>
            
            <!-- VERTICAL TIMELINE BENTO -->
            <div v-else class="p-5 h-full flex flex-col relative z-10 bg-gradient-to-b from-blue-50 to-white">
               
               <div class="flex justify-between items-start mb-4">
                 <div class="min-w-0 pr-2">
                     <span class="bg-[#0077B6] text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm uppercase tracking-wide whitespace-nowrap">Suivi de commande</span>
                     <p class="text-xs uppercase font-bold text-gray-500 mt-1 truncate">#{{ lastOrder.orderNumber || lastOrder.id.slice(0, 8).toUpperCase() }}</p>
                 </div>
                 <RouterLink :to="`/commande/suivi/${lastOrder.id}`" 
                    aria-label="Voir le suivi de commande"
                    class="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white flex items-center justify-center text-[#0077B6] hover:bg-[#0077B6] hover:text-white transition-colors shadow-sm shrink-0 -mt-1 -mr-1">
                    <ArrowRight class="w-4 h-4 md:w-5 md:h-5" />
                 </RouterLink>
               </div>

               <!-- Timeline Container -->
               <div class="flex-1 relative md:pl-2 flex flex-row justify-between items-start pt-4 md:pt-0 md:block md:space-y-6">
                  <!-- Line -->
                  <div class="absolute z-0 bg-gray-100 left-[15px] right-[15px] top-[30px] h-[2px] w-auto md:w-[2px] md:h-auto md:top-4 md:bottom-4 md:left-[23px] md:right-auto"></div>

                  <div v-for="(step, index) in orderSteps" :key="index" class="relative z-10 flex flex-col items-center gap-2 md:flex-row md:gap-4 group/item flex-1 md:flex-none">
                        <!-- Dot -->
                        <div class="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-4 transition-all duration-300"
                            :class="getStepColor(getStepState(index, getCurrentStepIndex(lastOrder.status)), step.color, lastOrder.status)">
                            <component :is="lastOrder.status === 'pending_reschedule' && index === 0 ? AlertTriangle : step.icon" class="h-4 w-4" />
                        </div>
                        
                        <!-- Text -->
                        <div class="flex flex-col items-center md:items-start text-center md:text-left">
                            <span class="text-xs font-bold transition-colors duration-300"
                                  :class="index <= getCurrentStepIndex(lastOrder.status) ? 'text-[#111]' : 'text-gray-400'">
                                {{ lastOrder.status === 'pending_reschedule' && index === 0 ? 'Action' : step.label }}
                            </span>
                        </div>
                  </div>
               </div>

               <div class="pt-4 border-t border-blue-50 mt-4 md:mt-0">
                <p class="text-xs text-center text-gray-500 font-medium">
                    Livraison prévue le <strong class="text-gray-700">{{ new Date(lastOrder.delivery?.date || Date.now()).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) }}</strong>
                  </p>
               </div>
            </div>
         </template>
         <template v-else-if="hasExtras">
             <div class="w-full h-full relative">
                 <!-- SlIDER WRAPPER -->
                 <div
                     class="w-full h-full flex flex-col transition-transform duration-700 ease-in-out will-change-transform transform-gpu"
                     :style="{ height: `${extrasList.length * 100}%`, transform: `translate3d(0, -${activeExtraSlide * (100 / extrasList.length)}%, 0)` }"
                 >
                     <RouterLink 
                         v-for="(extra, index) in extrasList" 
                         :key="index"
                         :to="authStore.isLoggedIn && authStore.user.type === 'b2b' ? '/commande/entreprise' : '/commande/maison'"
                         class="w-full relative shrink-0 overflow-hidden bg-gray-100 group/slide block"
                         :style="{ height: `${100 / extrasList.length}%` }"
                     >
                        <!-- Skeleton / Branded placeholder while loading -->
                        <div v-if="!extra.image || !isExtraImageLoaded(extra.image)" 
                             class="absolute inset-0 z-0 flex items-center justify-center"
                             :class="extra.image ? 'bg-gradient-to-br from-gray-50 to-gray-100' : 'bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900'">
                           <div class="flex flex-col items-center gap-3" :class="extra.image ? 'opacity-50' : 'opacity-40'">
                              <img src="/assets/legacy/logo_black.svg" :alt="extra.image ? 'Chargement...' : 'Clo\'Cook - Plats cuisinés artisanaux'" class="h-10 w-auto grayscale opacity-30" :class="extra.image ? 'animate-pulse' : ''" />
                              <p class="text-xs font-medium text-center leading-tight px-4" :class="extra.image ? 'text-gray-400' : 'text-gray-400'">Plats cuisinés artisanaux<br>Livraison à domicile à Annecy</p>
                           </div>
                        </div>

                        <img v-if="extra.image"
                            :src="getOptimizedExtraUrl(extra.image) || extra.image"
                            :srcset="getExtraSrcset(extra.image)"
                            sizes="(max-width: 768px) 100vw, 33vw"
                            @load="onExtraImageLoad(extra.image)"
                            class="absolute inset-0 w-full h-full object-cover transition-[opacity,scale,filter] duration-[1800ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover/slide:scale-[1.05] group-hover/slide:brightness-105 z-10"
                            :class="isExtraImageLoaded(extra.image) ? 'opacity-100' : 'opacity-0'"
                            :alt="extra.name" :loading="index === 0 ? 'eager' : 'lazy'" decoding="async" width="400" height="600" />

                        <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent z-10"></div>
                        
                        <div class="absolute top-4 left-4 z-20">
                           <span class="bg-white text-[#111] text-xs font-bold px-3 py-1 rounded-full shadow-sm uppercase tracking-wide">L'Extra du moment</span>
                        </div>
                        
                        <div class="absolute bottom-6 left-6 right-6 z-20 text-white">
                           <p class="font-bold leading-tight font-baloo text-xl mb-1 text-white drop-shadow-md">{{ extra.name }}</p>
                           <p class="text-xs opacity-90 line-clamp-2 mb-2 drop-shadow-sm">{{ extra.description }}</p>
                           <span class="font-bold text-[#ffd93e] text-3xl md:text-3xl drop-shadow-md">{{ extra.price }}</span>
                        </div>
                     </RouterLink>
                 </div>
                 
                 <!-- DOTS (Only if > 1) -->
                 <div v-if="extrasList.length > 1" class="absolute bottom-3 right-4 z-20 flex flex-col gap-1">
                    <button
                        v-for="(_, index) in extrasList"
                        :key="index"
                        @click.stop="activeExtraSlide = index"
                        aria-label="Changer de slide"
                        class="w-6 h-6 rounded-full transition-all duration-300 flex items-center justify-center"
                        :class="activeExtraSlide === index ? 'bg-[#ffd93e]/30' : 'bg-transparent'">
                        <span class="block w-2.5 h-2.5 rounded-full transition-all duration-300" :class="activeExtraSlide === index ? 'bg-[#ffd93e] scale-110 ring-1 ring-[#ffd93e]/50' : 'bg-white/40'"></span>
                    </button>
                 </div>
             </div>
         </template>
         <!-- Fallback : pas d'extra actif → parrainage standalone -->
         <template v-else>
             <div class="w-full h-full relative overflow-hidden bg-gray-100">
                <div v-if="!parrainageImageLoaded" class="absolute inset-0 bg-gradient-to-br from-[#e94591]/5 to-gray-100 z-0 flex items-center justify-center">
                   <div class="flex flex-col items-center gap-2 opacity-40">
                      <img src="/assets/legacy/logo_black.svg" alt="Clo'Cook Parrainage" class="h-8 w-auto animate-pulse grayscale opacity-30" />
                      <p class="text-xs text-gray-500 font-medium">Parrainage &amp; réductions</p>
                   </div>
                </div>
                <img :src="parrainageOffer.image"
                     class="absolute inset-0 w-full h-full object-cover transition-[opacity,scale,filter] duration-[1800ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:scale-[1.05] group-hover:brightness-105 z-0"
                     :class="parrainageImageLoaded ? 'opacity-100' : 'opacity-0'"
                     @load="parrainageImageLoaded = true"
                     alt="Parrainage" loading="lazy" decoding="async" width="800" height="600" />
                <div class="absolute inset-0 bg-gradient-to-t from-[#e94591]/90 to-transparent pointer-events-none"></div>
                <div class="absolute top-4 left-4 z-10">
                   <span class="bg-white text-[#e94591] text-xs font-bold px-3 py-1 rounded-full shadow-sm uppercase tracking-wide">Bon plan</span>
                </div>
                <div class="transition-transform duration-1000 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:-translate-y-1 absolute bottom-6 left-6 right-6 z-10 text-white">
                   <p class="font-bold leading-tight font-baloo text-xl mb-1">Parraine tes amis</p>
                   <p class="text-xs opacity-90 font-medium">Gagne -10% sur ta prochaine commande&nbsp;!</p>
                   <RouterLink :to="authStore.isLoggedIn ? '/compte/parrainage' : '/auth/connexion'" class="mt-3 inline-block bg-white text-[#e94591] px-4 py-2 rounded-full text-xs font-bold shadow-sm transition-all duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:bg-[#e94591] group-hover:text-white group-hover:shadow-lg group-hover:shadow-black/20 group-hover:pr-3 relative z-20">
                       J'en profite <ArrowRight class="inline w-3.5 h-3.5 -mt-0.5 ml-0.5 transition-transform duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:translate-x-1" />
                   </RouterLink>
                </div>
             </div>
         </template>
      </article>

      <!-- BLOCK 3: ENTREPRISE + PARRAINAGE SLIDER (si extras), sinon entreprise seul -->
      <article class="col-span-1 md:col-span-2 bg-white rounded-[32px] p-0 relative overflow-hidden h-[200px] md:h-auto shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[translate,box-shadow] duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] will-change-transform group border border-gray-100 min-h-[240px] flex flex-col">

         <div v-if="hasExtras"
            class="w-full h-full relative"
         >
             <!-- SLIDER WRAPPER -->
             <div
                class="w-full h-[200%] flex flex-col transition-transform duration-700 ease-in-out will-change-transform transform-gpu"
                :style="{ transform: `translate3d(0, -${activeSlide * 50}%, 0)` }"
             >
            
                <!-- ITEM 1: ENTREPRISE -->
                <div class="w-full h-1/2 relative shrink-0 overflow-hidden bg-gray-100">
                    <div v-if="!entrepriseImageLoaded" class="absolute inset-0 bg-gradient-to-br from-[#0077B6]/5 to-gray-100 z-0 flex items-center justify-center">
                       <div class="flex flex-col items-center gap-2 opacity-40">
                          <img src="/assets/legacy/logo_black.svg" alt="Clo'Cook Entreprise" class="h-8 w-auto animate-pulse grayscale opacity-30" />
                          <p class="text-xs text-gray-500 font-medium">Repas d'entreprise à Annecy</p>
                       </div>
                    </div>
                    <img :src="entrepriseOffer.image" 
                         class="absolute inset-0 w-full h-full object-cover transition-[opacity,scale,filter] duration-[1800ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:scale-[1.05] group-hover:brightness-105 z-0" 
                         :class="entrepriseImageLoaded ? 'opacity-100' : 'opacity-0'"
                         @load="entrepriseImageLoaded = true"
                         alt="Entreprise" loading="lazy" decoding="async" width="800" height="600" />
                    <div class="absolute inset-0 bg-gradient-to-t from-[#0077B6]/90 via-[#0077B6]/20 to-transparent pointer-events-none"></div>
                    
                    <!-- Badge Top Left -->
                    <div class="absolute top-4 left-4 z-10">
                       <div class="bg-white text-[#0077B6] text-xs font-bold px-3 py-1 rounded-full shadow-sm uppercase tracking-wide">Pour les Pros</div>
                    </div>

                    <div class="transition-transform duration-1000 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:-translate-y-1 absolute bottom-6 left-6 right-6 z-10 text-white">
                        <p class="font-bold leading-tight font-baloo text-xl mb-1">{{ entrepriseOffer.title }}</p>
                        <p class="text-xs opacity-90 font-medium line-clamp-2 md:line-clamp-none">{{ entrepriseOffer.subtitle }}</p>
                        
                        <RouterLink to="/entreprise" class="mt-3 inline-block bg-white text-[#0077B6] px-4 py-2 rounded-full text-xs font-bold shadow-sm transition-all duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:bg-[#0077B6] group-hover:text-white group-hover:shadow-lg group-hover:shadow-black/20 group-hover:pr-3 relative z-20">
                            Découvrir <ArrowRight class="inline w-3.5 h-3.5 -mt-0.5 ml-0.5 transition-transform duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:translate-x-1" />
                        </RouterLink>
                    </div>
                </div>

                <!-- ITEM 2: PARRAINAGE -->
                <div class="w-full h-1/2 relative shrink-0 overflow-hidden bg-gray-100">
                    <div v-if="!parrainageImageLoaded" class="absolute inset-0 bg-gradient-to-br from-[#e94591]/5 to-gray-100 z-0 flex items-center justify-center">
                       <div class="flex flex-col items-center gap-2 opacity-40">
                          <img src="/assets/legacy/logo_black.svg" alt="Clo'Cook Parrainage" class="h-8 w-auto animate-pulse grayscale opacity-30" />
                          <p class="text-xs text-gray-500 font-medium">Parrainage & réductions</p>
                       </div>
                    </div>
                    <img :src="parrainageOffer.image" 
                         class="absolute inset-0 w-full h-full object-cover transition-[opacity,scale,filter] duration-[1800ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:scale-[1.05] group-hover:brightness-105 z-0" 
                         :class="parrainageImageLoaded ? 'opacity-100' : 'opacity-0'"
                         @load="parrainageImageLoaded = true"
                         alt="Parrainage" loading="lazy" decoding="async" width="800" height="600" />
                    <div class="absolute inset-0 bg-gradient-to-t from-[#e94591]/90 to-transparent pointer-events-none"></div>
                    <div class="absolute top-4 left-4 z-10">
                    <span class="bg-white text-[#e94591] text-xs font-bold px-3 py-1 rounded-full shadow-sm uppercase tracking-wide">Bon plan</span>
                    </div>
                    <div class="transition-transform duration-1000 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:-translate-y-1 absolute bottom-6 left-6 right-6 z-10 text-white">
                    <p class="font-bold leading-tight font-baloo text-xl mb-1">Parraine tes amis</p>
                    <p class="text-xs opacity-90 font-medium">Gagne -10% sur ta prochaine commande&nbsp;!</p>
                    <RouterLink :to="authStore.isLoggedIn ? '/compte/parrainage' : '/auth/connexion'" class="mt-3 inline-block bg-white text-[#e94591] px-4 py-2 rounded-full text-xs font-bold shadow-sm transition-all duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:bg-[#e94591] group-hover:text-white group-hover:shadow-lg group-hover:shadow-black/20 group-hover:pr-3 relative z-20">
                        J'en profite <ArrowRight class="inline w-3.5 h-3.5 -mt-0.5 ml-0.5 transition-transform duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:translate-x-1" />
                    </RouterLink>
                    </div>
                </div>

             </div>
         </div>

         <!-- DOTS (slider entreprise + parrainage) -->
         <div v-if="hasExtras" class="absolute bottom-3 right-4 z-20 flex flex-col gap-1">
            <button @click.stop="scrollToSlide(0)"
                aria-label="Voir l'offre entreprise"
                class="w-6 h-6 rounded-full transition-all duration-300 flex items-center justify-center"
                :class="activeSlide === 0 ? 'bg-white/30' : 'bg-transparent'">
                <span class="block w-2.5 h-2.5 rounded-full transition-all duration-300" :class="activeSlide === 0 ? 'bg-white scale-110 ring-1 ring-white/50' : 'bg-white/40'"></span>
            </button>
            <button @click.stop="scrollToSlide(1)"
                aria-label="Voir l'offre parrainage"
                class="w-6 h-6 rounded-full transition-all duration-300 flex items-center justify-center"
                :class="activeSlide === 1 ? 'bg-white/30' : 'bg-transparent'">
                <span class="block w-2.5 h-2.5 rounded-full transition-all duration-300" :class="activeSlide === 1 ? 'bg-white scale-110 ring-1 ring-white/50' : 'bg-white/40'"></span>
            </button>
         </div>

         <!-- Fallback : pas d'extra actif → entreprise standalone (le parrainage migre dans Block 2) -->
         <div v-else class="w-full h-full relative overflow-hidden bg-gray-100">
            <div v-if="!entrepriseImageLoaded" class="absolute inset-0 bg-gradient-to-br from-[#0077B6]/5 to-gray-100 z-0 flex items-center justify-center">
               <div class="flex flex-col items-center gap-2 opacity-40">
                  <img src="/assets/legacy/logo_black.svg" alt="Clo'Cook Entreprise" class="h-8 w-auto animate-pulse grayscale opacity-30" />
                  <p class="text-xs text-gray-500 font-medium">Repas d'entreprise à Annecy</p>
               </div>
            </div>
            <img :src="entrepriseOffer.image"
                 class="absolute inset-0 w-full h-full object-cover transition-[opacity,scale,filter] duration-[1800ms] ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:scale-[1.05] group-hover:brightness-105 z-0"
                 :class="entrepriseImageLoaded ? 'opacity-100' : 'opacity-0'"
                 @load="entrepriseImageLoaded = true"
                 alt="Entreprise" loading="lazy" decoding="async" width="800" height="600" />
            <div class="absolute inset-0 bg-gradient-to-t from-[#0077B6]/90 via-[#0077B6]/20 to-transparent pointer-events-none"></div>
            <div class="absolute top-4 left-4 z-10">
               <div class="bg-white text-[#0077B6] text-xs font-bold px-3 py-1 rounded-full shadow-sm uppercase tracking-wide">Pour les Pros</div>
            </div>
            <div class="transition-transform duration-1000 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:-translate-y-1 absolute bottom-6 left-6 right-6 z-10 text-white">
               <p class="font-bold leading-tight font-baloo text-xl mb-1">{{ entrepriseOffer.title }}</p>
               <p class="text-xs opacity-90 font-medium line-clamp-2 md:line-clamp-none">{{ entrepriseOffer.subtitle }}</p>
               <RouterLink to="/entreprise" class="mt-3 inline-block bg-white text-[#0077B6] px-4 py-2 rounded-full text-xs font-bold shadow-sm transition-all duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:bg-[#0077B6] group-hover:text-white group-hover:shadow-lg group-hover:shadow-black/20 group-hover:pr-3 relative z-20">
                   Découvrir <ArrowRight class="inline w-3.5 h-3.5 -mt-0.5 ml-0.5 transition-transform duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] group-hover:translate-x-1" />
               </RouterLink>
            </div>
         </div>

      </article>

    </div>

    <!-- SECTION: ACCESS CHOICES -->
    <nav class="grid gap-4 grid-cols-1 md:grid-cols-2 pb-1" aria-label="Choix de l'offre">
      <RouterLink :to="authStore.isLoggedIn ? '/compte/commandes' : '/concept'"
        class="group rounded-[32px] border border-[#ffd93e]/40 bg-gradient-to-br from-[#ffd93e]/25 via-white to-white p-5 md:p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[translate,scale,box-shadow] duration-300 relative overflow-hidden">
        <div class="relative z-10 flex justify-between items-center">
          <div class="flex items-center gap-3">
            <IconDisc color="yellow" variant="solid" size="md" class="group-hover:scale-110 transition-transform">
              <component :is="authStore.isLoggedIn ? Package : Armchair" />
            </IconDisc>
            <div>
              <template v-if="!authStore.isLoggedIn">
                <h2 class="text-xl font-extrabold text-[#111] font-baloo">Clo'Cook Maison</h2>
                <p class="text-sm text-gray-500">Simple, personnalisé, sans stress.</p>
              </template>
              <template v-else>
                <h2 class="text-xl font-extrabold text-[#111] font-baloo">Mes Commandes</h2>
                <p class="text-sm text-gray-500">Voir l'historique et les factures.</p>
              </template>
            </div>
          </div>
          <ArrowRight class="h-5 w-5 text-gray-400 group-hover:text-[#111] transition-colors shrink-0" />
        </div>
      </RouterLink>

      <!-- Si user connecté + profil non affiné : pousse l'affinage à la place de Mon Compte -->
      <RouterLink v-if="authStore.isLoggedIn && leastTunedProfile"
        :to="`/compte/profils/${leastTunedProfile.id}/gouts`"
        class="group rounded-[32px] bg-gradient-to-br from-[#e94591]/10 via-white to-[#ffd93e]/10 border border-[#e94591]/20 p-5 md:p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[translate,scale,box-shadow] duration-300 relative overflow-hidden">
        <div class="relative z-10 flex items-center gap-3">
          <IconDisc color="pink" variant="solid" size="md" :delay="90" class="group-hover:scale-110 transition-transform">
            <Sparkles />
          </IconDisc>
          <div class="min-w-0 flex-1">
            <h2 class="text-xl font-extrabold text-[#111] font-baloo truncate">Affine les goûts de {{ leastTunedProfile.name }}</h2>
            <div class="flex items-center gap-2 mt-1">
              <div class="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden max-w-[160px]">
                <div class="h-full bg-[#e94591] rounded-full transition-all duration-500" :style="{ width: leastTunedProfile.score + '%' }"></div>
              </div>
              <span class="text-xs font-bold text-gray-500 tabular-nums">{{ leastTunedProfile.score }}%</span>
            </div>
          </div>
          <ArrowRight class="h-5 w-5 text-gray-400 group-hover:text-[#e94591] transition-colors shrink-0" />
        </div>
      </RouterLink>

      <!-- User connecté + profils tous à 100% : pousse atelier (si déjà commandé) ou 1re commande (sinon) -->
      <RouterLink v-else-if="authStore.isLoggedIn && lastOrder && ateliersStore.hasUpcoming"
        to="/ateliers"
        class="group rounded-[32px] bg-gradient-to-br from-[#e94591]/15 via-white to-[#ffd93e]/10 border border-[#e94591]/20 p-5 md:p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[translate,scale,box-shadow] duration-300 relative overflow-hidden">
        <div class="relative z-10 flex justify-between items-center">
          <div class="flex items-center gap-3">
            <IconDisc color="pink" variant="solid" size="md" :delay="90" class="group-hover:scale-110 transition-transform">
              <GraduationCap />
            </IconDisc>
            <div>
              <h2 class="text-xl font-extrabold text-[#111] font-baloo">Ateliers Clo'Cook</h2>
              <p class="text-sm text-gray-500">Découvre les prochains ateliers nutrition.</p>
            </div>
          </div>
          <ArrowRight class="h-5 w-5 text-gray-400 group-hover:text-[#111] transition-colors shrink-0" />
        </div>
      </RouterLink>

      <RouterLink v-else-if="authStore.isLoggedIn"
        to="/commande/maison"
        class="group rounded-[32px] bg-gradient-to-br from-[#e94591]/15 via-white to-[#ffd93e]/10 border border-[#e94591]/20 p-5 md:p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[translate,scale,box-shadow] duration-300 relative overflow-hidden">
        <div class="relative z-10 flex justify-between items-center">
          <div class="flex items-center gap-3">
            <IconDisc color="pink" variant="solid" size="md" :delay="90" class="group-hover:scale-110 transition-transform">
              <ShoppingBag />
            </IconDisc>
            <div>
              <h2 class="text-xl font-extrabold text-[#111] font-baloo">Profil prêt</h2>
              <p class="text-sm text-gray-500">Lance ta première commande.</p>
            </div>
          </div>
          <ArrowRight class="h-5 w-5 text-gray-400 group-hover:text-[#e94591] transition-colors shrink-0" />
        </div>
      </RouterLink>

      <RouterLink v-else
        to="/entreprise"
        class="group rounded-[32px] bg-gradient-to-br from-[#0077B6]/10 via-white to-[#0077B6]/10 border border-white/60 p-5 md:p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[translate,scale,box-shadow] duration-300 relative overflow-hidden">
        <div class="relative z-10 flex justify-between items-center">
          <div class="flex items-center gap-3">
            <IconDisc color="blue" variant="solid" size="md" :delay="90" class="group-hover:scale-110 transition-transform">
              <Building2 />
            </IconDisc>
            <div>
              <h2 class="text-xl font-extrabold text-[#111] font-baloo">Clo'Cook Entreprises</h2>
              <p class="text-sm text-gray-500">Repas collectifs et pilotage simplifié.</p>
            </div>
          </div>
          <ArrowRight class="h-5 w-5 text-gray-400 group-hover:text-[#111] transition-colors shrink-0" />
        </div>
      </RouterLink>
    </nav>

    <AtelierHomeSection />

    <!-- COMMENT ÇA MARCHE : composant partagé avec la page Maison ; ici le Reel de présentation à droite des étapes -->
    <HowItWorksFlyer media="reel" />

    <!-- SECTION: FONDATRICE (placée juste après "Comment ça marche" pour humaniser
         l'expérience le plus tôt possible : la confiance vient du visage humain) -->
    <section id="chloe" class="bg-white rounded-[32px] p-6 md:p-12 shadow-sm border border-gray-100 flex flex-col md:flex-row items-center gap-8 md:gap-12 overflow-hidden relative scroll-mt-20" style="content-visibility: auto; contain-intrinsic-size: auto 400px">
       <!-- Photo container: vertical for sport vibe -->
       <div class="w-full md:w-1/3 h-48 md:h-80 rounded-[24px] overflow-hidden shrink-0 border-4 border-white shadow-lg relative z-10 rotate-2 hover:rotate-0 transition-transform duration-500 bg-gray-200">
          <picture>
            <source srcset="/img/hero-mobile.webp" media="(max-width: 768px)" type="image/webp" />
            <img src="/img/hero.webp" alt="Chloé, Fondatrice de Clo'Cook" class="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700" fetchpriority="high" width="600" height="800" />
          </picture>
       </div>
       <div class="flex-1 text-center md:text-left relative z-10">
          <Eyebrow tone="maison" class="mb-3">L'âme de Clo'Cook</Eyebrow>
          <BrandTitle as="h2" tone="maison" text="Le mot de la fondatrice" keyword="fondatrice" class="mb-6" />

          <div class="space-y-4 text-gray-600 leading-relaxed text-sm md:text-base">
             <p>
                "Je m'appelle <strong class="text-[#111]">Chloé</strong>, je suis Diététicienne-Nutritionniste. J'ai grandi entre les casseroles de ma mère et les balades en pleine nature."
             </p>
             <p>
                "J'aime courir, randonner et partager des repas simples entre amis. C'est cette énergie positive que je mets dans chaque plat Clo'Cook : montrer qu'une alimentation saine peut être <strong class="text-[#111]">gourmande et accessible à tous</strong>."
             </p>
          </div>

          <div class="mt-6 flex items-center justify-center md:justify-start gap-3">
             <div class="h-px w-12 bg-[#ffd93e]"></div>
             <p class="text-sm font-bold text-[#111] uppercase tracking-widest">
                Chloé, fondatrice
             </p>
          </div>
       </div>

       <!-- Decorative Elements -->
       <div class="absolute -top-20 -right-20 w-64 h-64 bg-[#ffd93e] rounded-full blur-[80px] opacity-20"></div>
       <div class="absolute bottom-0 left-20 w-32 h-32 bg-[#e94591] rounded-full blur-[60px] opacity-10"></div>
    </section>

    <!-- SECTION: CONCEPT SOUS-VIDE : deux colonnes, texte à gauche (eyebrow,
         titre, pitch, trois arguments, lien), visuel à droite (démo sachet /
         assiette en fondu si des plats sont marqués vitrine, sinon la photo). -->
    <section id="sous-vide" class="bg-gradient-to-br from-[#0077B6]/10 to-white rounded-[32px] p-6 sm:p-8 md:p-12 border border-[#0077B6]/20 relative overflow-hidden scroll-mt-20" style="content-visibility: auto; contain-intrinsic-size: 1px 560px;">
       <div class="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-12 items-center relative z-10">
          <div class="md:col-span-3">
             <Eyebrow tone="bleu" class="mb-3">Du sous-vide à l'assiette</Eyebrow>
             <BrandTitle as="h2" tone="bleu" text="Livré sous-vide, prêt en 3 min." keyword="prêt en 3 min." class="mb-3" />
             <p class="text-sm md:text-base text-gray-600 leading-relaxed max-w-xl">
                Cuisiné maison, scellé <strong class="text-[#111]">sous vide</strong> pour garder saveurs et nutriments, puis livré chez toi. Tu réchauffes, tu dresses. Des repas qui se conservent <strong class="text-[#111]">jusqu'à 10 jours au frais</strong> et plusieurs semaines au congélateur, sans aucun additif.
             </p>
             <ul class="mt-6 space-y-3">
                <li class="flex items-center gap-3 text-sm md:text-base text-[#111] font-bold">
                   <IconDisc color="blue" variant="solid" size="sm" :pop="false"><Check /></IconDisc> Jusqu'à 10 jours au frais
                </li>
                <li class="flex items-center gap-3 text-sm md:text-base text-[#111] font-bold">
                   <IconDisc color="blue" variant="solid" size="sm" :pop="false"><Check /></IconDisc> Textures et vitamines intactes
                </li>
                <li class="flex items-center gap-3 text-sm md:text-base text-[#111] font-bold">
                   <IconDisc color="blue" variant="solid" size="sm" :pop="false"><Check /></IconDisc> Compatible bain-marie et congélation
                </li>
             </ul>
             <p class="inline-flex items-center gap-2 mt-6 bg-white border border-[#0077B6]/20 text-[#0077B6] text-xs md:text-sm font-bold px-4 py-2 rounded-full">
                <Utensils class="w-4 h-4 shrink-0" /> On adapte tes repas à partir de plus de 200 recettes.
             </p>
             <div class="mt-6">
                <RouterLink to="/conservation" class="text-[#0077B6] font-bold text-sm hover:underline inline-flex items-center gap-1">
                   En savoir plus sur notre méthode <ArrowRight class="w-4 h-4" />
                </RouterLink>
             </div>
          </div>
          <div class="md:col-span-2">
             <div v-if="sousVideShowcase.length > 0" class="max-w-xs mx-auto">
                <BeforeAfterFade
                   :plate-image="sousVideShowcase[0].image"
                   :sous-vide-image="sousVideShowcase[0].sousVideImage"
                   :dish-name="sousVideShowcase[0].name || ''"
                   aspect="portrait" />
             </div>
             <!-- Repli : une photo de plat du catalogue (même sélection que le bandeau final) -->
             <div v-else class="max-w-xs mx-auto rounded-[28px] overflow-hidden shadow-lg border-[3px] border-white -rotate-3 bg-[#efe9db]">
                <img :src="sousVideFallbackImage" alt="Plat Clo'Cook" width="640" height="800" loading="lazy" decoding="async" class="w-full aspect-[4/5] object-cover" />
             </div>
          </div>
       </div>

       <!-- Decorative blobs -->
       <div class="absolute -right-10 -bottom-10 w-48 h-48 bg-[#0077B6] rounded-full blur-[60px] opacity-10 pointer-events-none"></div>
       <div class="absolute -left-10 top-10 w-32 h-32 bg-[#ffd93e] rounded-full blur-[60px] opacity-10 pointer-events-none"></div>
    </section>

    <!-- TESTIMONIALS -->
    <section id="avis" class="py-4 pb-8 scroll-mt-20">
       <div class="text-center mb-8">
          <BrandTitle as="h2" tone="maison" text="Les derniers retours clients" keyword="retours clients" />
          <!-- Badge note moyenne (calculée à partir des avis réels Firestore).
               Affichage seulement si on a au moins 1 avis valide. -->
          <div v-if="reviewsStats" class="inline-flex items-center gap-2 mt-3 bg-yellow-50 border border-[#ffd93e]/40 rounded-full px-4 py-1.5">
             <div class="flex text-[#ffd93e]">
                <Star v-for="n in 5" :key="n" class="w-3.5 h-3.5 fill-current" />
             </div>
             <span class="text-sm font-extrabold text-[#111]">{{ reviewsStats.average }}/5</span>
             <span class="text-xs text-gray-600 font-semibold">sur {{ reviewsCountLabel }} avis clients</span>
          </div>
       </div>
       <div v-if="latestReviews.length" ref="testimonialsCarousel"
            @scroll.passive="_onTestimonialsScroll"
            class="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-4 px-4 py-2">
          <div v-for="avis in latestReviews" :key="avis.id" data-card class="bg-white rounded-[24px] p-5 shadow-sm border border-gray-100 flex flex-col gap-3 hover:-translate-y-1 transition-transform duration-300 shrink-0 w-[85%] sm:w-[320px] snap-center">
             <!-- Plat (miniature) + client tronqué -->
             <div class="flex items-center gap-3">
                <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-50 to-yellow-50 overflow-hidden shrink-0 flex items-center justify-center">
                   <img v-if="avis.dishImage" :src="avis.dishImage" :alt="avis.dishName" loading="lazy" class="w-full h-full object-cover" />
                   <Utensils v-else class="w-5 h-5 text-gray-300" />
                </div>
                <div class="min-w-0">
                   <p class="text-sm font-bold text-[#111] leading-tight line-clamp-1">{{ avis.dishName }}</p>
                   <p class="text-xs text-gray-500">{{ avis.name }}</p>
                </div>
             </div>
             <!-- Avis -->
             <p class="text-gray-600 text-sm italic flex-1 leading-snug">"{{ avis.text }}"</p>
             <!-- Étoiles + date -->
             <div class="flex items-center justify-between pt-1 border-t border-gray-50">
                <div class="flex text-[#ffd93e] pt-1">
                   <Star v-for="n in 5" :key="n" class="w-4 h-4" :class="n <= avis.rating ? 'fill-current' : 'text-gray-200'" />
                </div>
                <span class="text-[11px] text-gray-400 pt-1">{{ avis.date }}</span>
             </div>
          </div>
       </div>
       <!-- Dots de navigation du carrousel d'avis -->
       <div v-if="latestReviews.length > 1" class="flex justify-center gap-2 mt-4">
          <button v-for="n in latestReviews.length" :key="n"
                  type="button"
                  @click="_slideTo(testimonialsCarousel, n - 1)"
                  :aria-label="`Avis ${n}`"
                  :class="['transition-all rounded-full',
                           (n - 1) === testimonialsActiveIndex ? 'w-6 h-2 bg-[#e94591]' : 'w-2 h-2 bg-gray-300']"></button>
       </div>
    </section>

    <!-- (section "Va plus loin avec Clo'Cook" déplacée plus bas, après le CTA) -->

    <!-- CTA FINAL : photo en arrière-plan + dégradé jaune Clo'Cook dense côté
         GAUCHE (zone du texte). Sur le jaune, texte encre et bouton encre. -->
    <section class="rounded-[32px] relative overflow-hidden min-h-[280px] md:min-h-[240px] flex items-center">
       <!-- Photo : couvre tout sur mobile (voile par-dessus), limitée à la
            moitié droite sur desktop pour ne pas écraser le cadre. -->
       <img :src="ctaBackgroundImage" :alt="''" aria-hidden="true"
            class="absolute inset-y-0 right-0 h-full w-full md:w-1/2 object-cover"
            loading="lazy" decoding="async" />
       <!-- Dégradé jaune Clo'Cook : voile global sur mobile, jaune dense
            jusqu'à 50% (zone texte) puis fade sur la photo en desktop. -->
       <div class="absolute inset-0 pointer-events-none md:hidden"
            style="background: linear-gradient(to bottom, rgba(255,217,62,0.88) 0%, rgba(255,217,62,0.78) 100%);"></div>
       <div class="absolute inset-0 pointer-events-none hidden md:block"
            style="background: linear-gradient(to right, #ffd93e 0%, #ffd93e 50%, rgba(255,217,62,0.7) 65%, rgba(255,217,62,0.4) 85%, rgba(255,217,62,0.6) 100%);"></div>

       <!-- Contenu centré horizontalement et verticalement -->
       <div class="relative z-10 w-full px-6 py-10 md:px-12 md:py-14 text-center">
          <div class="max-w-xl mx-auto">
             <BrandTitle as="h2" text="Prêt(e) à goûter ?" class="mb-3" />
             <p class="text-[#111]/80 text-sm md:text-base mb-6 font-medium">
                Renseigne tes goûts, choisis ton créneau à Annecy. <strong class="text-[#111]">On compose ton menu</strong> et on s'occupe du reste.
             </p>
             <RouterLink :to="authStore.isLoggedIn && authStore.user.type === 'b2b' ? '/commande/entreprise' : '/commande/maison'"
                         class="inline-flex items-center justify-center gap-2 bg-[#111] hover:bg-black text-white px-7 py-3.5 rounded-[20px] font-extrabold text-sm md:text-base shadow-lg shadow-black/15 active:scale-95 hover:scale-[1.02] transition-all">
                Je commande
                <ArrowRight class="w-4 h-4" />
             </RouterLink>
             <p class="text-gray-700 text-xs mt-4">
                Sans engagement · À partir de 14 € le repas, livraison incluse
             </p>
          </div>
       </div>

       <!-- Décor lumière rose subtile en haut gauche (touch d'identité) -->
       <div class="absolute -top-12 -left-12 w-40 h-40 bg-[#e94591] rounded-full blur-[60px] opacity-20 pointer-events-none"></div>
    </section>

    <!-- BENTO : VA PLUS LOIN AVEC CLO'COOK (Ateliers + Consultation diététique)
         Placée APRÈS le CTA principal : prolongement de l'offre pour ceux
         qui veulent aller plus loin que les repas livrés. Marge top
         renforcée pour bien séparer du CTA "Prêt(e) à goûter ?". -->
    <section class="space-y-4 !mt-10 md:!mt-16">
       <SectionHeading tone="maison" title="Va plus loin avec Clo'Cook" keyword="plus loin"
                       subtitle="Apprends, échange, progresse. On t'écoute et on t'accompagne au-delà de l'assiette." />
       <div ref="vaplusloinCarousel"
            @scroll.passive="_onVaplusloinScroll"
            class="md:grid md:grid-cols-2 md:gap-4 flex md:flex-none gap-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none scrollbar-hide -mx-4 md:mx-0 px-4 md:px-0 py-2 md:py-0">
          <!-- Ateliers nutrition (carte jaune : ton maison) -->
          <RouterLink to="/ateliers" class="group relative bg-gradient-to-br from-[#ffd93e]/25 via-white to-white rounded-[32px] p-6 md:p-8 border border-[#ffd93e]/40 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[translate,scale,box-shadow] duration-300 overflow-hidden flex flex-col shrink-0 w-full snap-center md:shrink md:w-auto md:snap-align-none">
             <IconDisc color="yellow" variant="solid" size="md" class="mb-4">
                <GraduationCap />
             </IconDisc>
             <BrandTitle as="h3" text="Ateliers nutrition à Annecy" class="mb-2" />
             <p class="text-sm text-gray-600 leading-relaxed mb-4 flex-1">
                Des ateliers en petit groupe avec une diététicienne. Ludiques, interactifs, 100&nbsp;% participatifs.
             </p>
             <span class="inline-flex items-center gap-2 text-sm font-bold text-[#111] group-hover:gap-3 transition-all">
                Voir les ateliers <ArrowRight class="w-4 h-4" />
             </span>
             <div class="absolute -bottom-10 -right-10 w-32 h-32 bg-[#ffd93e] rounded-full blur-[50px] opacity-30 pointer-events-none"></div>
          </RouterLink>

          <!-- Consultation diététique (carte rose) -->
          <RouterLink to="/contact?sujet=consultation" class="group relative bg-gradient-to-br from-[#e94591]/10 via-white to-white rounded-[32px] p-6 md:p-8 border border-[#e94591]/20 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[translate,scale,box-shadow] duration-300 overflow-hidden flex flex-col shrink-0 w-full snap-center md:shrink md:w-auto md:snap-align-none">
             <IconDisc color="pink" variant="solid" size="md" :delay="90" class="mb-4">
                <Salad />
             </IconDisc>
             <BrandTitle as="h3" text="Consultation diététique sur RDV" class="mb-2" />
             <p class="text-sm text-gray-600 leading-relaxed mb-4 flex-1">
                Bilan personnalisé et menus sur-mesure adaptés à tes objectifs (santé, poids, énergie). À domicile ou en visio.
             </p>
             <span class="inline-flex items-center gap-2 text-sm font-bold text-[#e94591] group-hover:gap-3 transition-all">
                Prendre rendez-vous <ArrowRight class="w-4 h-4" />
             </span>
             <div class="absolute -bottom-10 -right-10 w-32 h-32 bg-[#e94591] rounded-full blur-[50px] opacity-20 pointer-events-none"></div>
          </RouterLink>
       </div>
       <!-- Dots mobile pour Va plus loin -->
       <div class="md:hidden flex justify-center gap-2 mt-4">
          <button v-for="n in 2" :key="n"
                  type="button"
                  @click="_slideTo(vaplusloinCarousel, n - 1)"
                  :aria-label="`Section ${n}`"
                  :class="['transition-all rounded-full',
                           (n - 1) === vaplusloinActiveIndex ? 'w-6 h-2 bg-[#e94591]' : 'w-2 h-2 bg-gray-300']"></button>
       </div>
    </section>

  </div>
</template>

<style scoped>
/* Bouton du hero : au survol, le disque rose gonfle et inonde la pilule,
   le texte passe en blanc, le disque s'inverse et la flèche part à droite. */
.btn-flyer { position: relative; overflow: hidden; isolation: isolate; }
.btn-flyer::before {
  content: '';
  position: absolute;
  left: 0.5rem;
  top: 50%;
  width: 2.5rem;
  height: 2.5rem;
  margin-top: -1.25rem;
  border-radius: 9999px;
  background: #e94591;
  z-index: -1;
  transform: scale(0);
  transition: transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.btn-flyer:hover::before { transform: scale(12); }
.btn-flyer:hover { color: #fff; }
.btn-flyer-disc { transition: background-color 0.3s ease 0.1s, color 0.3s ease 0.1s, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
.btn-flyer:hover .btn-flyer-disc { background: #fff; color: #e94591; transform: rotate(-12deg) scale(1.08); }
.btn-flyer-arrow { transition: transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1), color 0.3s ease; }
.btn-flyer:hover .btn-flyer-arrow { color: #fff; transform: translateX(0.3rem); }
@media (prefers-reduced-motion: reduce) {
  .btn-flyer::before, .btn-flyer-disc, .btn-flyer-arrow { transition: none; }
}

/* Transition entre 2 plats successifs dans le rotateur sous-vide mobile.
   Fondu doux sans déplacement pour rester lisible et calme. */
.dish-rotate-enter-active,
.dish-rotate-leave-active {
  transition: opacity 600ms ease-in-out;
}
.dish-rotate-enter-from,
.dish-rotate-leave-to {
  opacity: 0;
}
</style>